import { fromBech32, toBech32 } from '@cosmjs/encoding';
import { Decimal } from '@cosmjs/math';
import { GasPrice, StargateClient } from '@cosmjs/stargate';
import { Tendermint34Client } from '@cosmjs/tendermint-rpc';
import { createHash } from 'crypto';
import { ethers } from 'ethers';

import { APP_CONFIG } from 'app-config';
import { ExplorerUrlType } from 'app/types/providers';

import { ELYS_CHAIN } from 'chains/chains.constants';
import {
  ChainCurrency,
  ChainFeeCurrency,
  TokenMinimalDenomAny,
} from 'chains/chains.types';
import {
  COSMOS_ASSETS,
  COSMOS_ASSETS_BY_DENOM,
  COSMOS_CHAINS,
} from 'chains/cosmos/cosmos.assets';
import {
  CosmosChainId,
  CosmosTokenMinimalDenom,
} from 'chains/cosmos/cosmos.types';
import { EVM_CHAINS } from 'chains/evm/evm.assets';
import { EVMChainId } from 'chains/evm/evm.types';

const {BASE_URL} = APP_CONFIG;

export const explorerUrl = (
  chainId: CosmosChainId | EVMChainId,
  type: ExplorerUrlType,
  value?: string,
) => {
  const {explorerURL} = {...COSMOS_CHAINS, ...EVM_CHAINS}[chainId];
  if (!value) {
    return explorerURL[type as keyof typeof explorerURL];
  }

  return explorerURL[type as keyof typeof explorerURL].replace(`{${String(type)}}`, value);
};

/**
 * Formats standard cosmos address to bech32 format. (coin type 118)
 */
export const formatAddress = (chainId: CosmosChainId, address: string) => {
  const {addressPrefix} = COSMOS_CHAINS[chainId];

  return toBech32(addressPrefix, fromBech32(address).data);
};

export const validateAddress = (chainId: CosmosChainId, address: string) => {
  try {
    const {addressPrefix} = COSMOS_CHAINS[chainId];

    return (
      address.startsWith(addressPrefix) && !!formatAddress(chainId, address)
    );
  } catch {
    return false;
  }
};

export const createStargateClient = (
  chain: CosmosChainId,
): Promise<StargateClient> => {
  return StargateClient.connect(COSMOS_CHAINS[chain].rpcURL);
};

export const createTendermintClient = (
  chain: CosmosChainId,
): Promise<Tendermint34Client> => {
  return Tendermint34Client.connect(COSMOS_CHAINS[chain].rpcURL);
};

/* export const createIbcExtension = async (
  chain: ChainId,
): Promise<IbcExtension> => {
  const client = await createTendermintClient(chain);
  const queryClient = new QueryClient(client);

  return setupIbcExtension(queryClient);
}; */

export const getChainWalletInfo = (chainId: CosmosChainId) => {
  const {
    addressPrefix,
    bip44,
    chainName,
    rpcURL,
    restURL,
    currencies,
    feeCurrencies = [],
  } = COSMOS_CHAINS[chainId];

  const stakedCurrency = currencies.find(
    currency => currency && currency.isStakeCurrency,
  );

  if (!stakedCurrency) {
    console.warn(`No staked currency found for ${chainId}.`);
  }

  return {
    chainId: chainId,
    chainName: chainName,
    rpc: rpcURL,
    rest: restURL,
    bip44: bip44 ?? {
      coinType: 118,
    },
    bech32Config: bech32Prefix(addressPrefix),

    currencies: currencies.map(currency => formatCurrency(currency)),

    feeCurrencies: [
      ...currencies.filter(e => e.isFeeCurrency),
      ...feeCurrencies,
    ].map(currency => formatFeeCurrency(currency)),

    stakeCurrency: stakedCurrency ? formatCurrency(stakedCurrency) : undefined!,
  };
};

const bech32Prefix = (addressPrefix: string) => {
  return {
    bech32PrefixAccAddr: addressPrefix,
    bech32PrefixAccPub: `${addressPrefix}pub`,
    bech32PrefixValAddr: `${addressPrefix}valoper`,
    bech32PrefixValPub: `${addressPrefix}valoperpub`,
    bech32PrefixConsAddr: `${addressPrefix}valcons`,
    bech32PrefixConsPub: `${addressPrefix}valconspub`,
  };
};

const formatCurrency = (currency: ChainCurrency) => ({
  coinDenom: currency.coinDenom,
  coinMinimalDenom: currency.coinMinimalDenom,
  coinDecimals: currency.coinDecimals,
  coinGeckoId: currency.coinGeckoId,
  coinImageUrl: formatTokenUrl(currency.coinImageUrl),
});

const formatFeeCurrency = (currency: ChainFeeCurrency) => ({
  coinDenom: currency.coinDenom,
  coinMinimalDenom: currency.coinMinimalDenom,
  coinDecimals: currency.coinDecimals,
  coinGeckoId: currency.coinGeckoId,
  coinImageUrl: formatTokenUrl(currency.coinImageUrl),
  gasPriceStep: currency.gasPriceStep ?? {
    low: 0.01,
    average: 0.025,
    high: 0.03,
  },
});

const formatTokenUrl = (url?: string) => {
  if (url?.startsWith('http')) {
    return url;
  }

  return `${BASE_URL}/${url}`;
};

export const hashIbcDenom = (value: string): `ibc/${string}` => {
  const hash = createHash('sha256').update(value).digest('hex').toUpperCase();

  return `ibc/${hash}`;
};

export const lookupElysAssetByIcbDenom = (denom: string) => {
  return COSMOS_ASSETS.find(
    asset =>
      getElysIcbDenom(ELYS_CHAIN.chainId, asset.coinMinimalDenom) === denom ||
      asset.coinMinimalDenom === denom,
  )!;
};

export const getElysIcbDenom = (
  chainId: CosmosChainId,
  coinMinimalDenom: TokenMinimalDenomAny,
) => {
  // check if is elys token or if it is devnet
  if (
    APP_CONFIG.CHAIN_NETWORK === 'devnet' ||
    (chainId === ELYS_CHAIN.chainId &&
      ELYS_CHAIN.currencies[0].coinMinimalDenom === coinMinimalDenom)
  ) {
    return coinMinimalDenom;
  }

  return getIbcDenomByChain(chainId, coinMinimalDenom);
};

export const getIbcDenomByChain = (
  chainId: CosmosChainId,
  denom: TokenMinimalDenomAny,
): string => {
  const elysCurencies: CosmosTokenMinimalDenom[] = ELYS_CHAIN.currencies.map(
    currency => currency.coinMinimalDenom,
  );

  if (
    chainId !== ELYS_CHAIN.chainId ||
    elysCurencies.includes(denom) ||
    chainId === 'elysdevnet-1'
  ) {
    return denom;
  }

  const sourceChain = COSMOS_CHAINS[COSMOS_ASSETS_BY_DENOM[denom]?.chainId];

  return hashIbcDenom(`transfer/${sourceChain?.channel?.source}/${denom}`);
};

/**
 * Useful to send cosmos addresses to EVM chains.
 *
 * @param address A valid cosmos address
 * @returns Buffer
 */
export function getBytes32(address: string) {
  const ZEROS = Buffer.alloc(12);
  const bytes = Buffer.from(fromBech32(address).data);
  const buffer = Buffer.concat([ZEROS, bytes]);

  return `0x${buffer.toString('hex')}`;
}

export function toHexString(word: string) {
  return ethers.zeroPadValue(ethers.toUtf8Bytes(word), 32);
}

export function getShortAddress(
  address: string,
  opts?: {prefixLength?: number; suffixLength?: number},
) {
  return (
    address.substring(0, opts?.prefixLength ?? 5) +
    '...' +
    address.substring(
      address.length - (opts?.suffixLength ?? 3),
      address.length,
    )
  );
}

export function calculateGasPrice(chainId: CosmosChainId): GasPrice {
  const {currencies} = COSMOS_CHAINS[chainId] ?? {};

  if (!currencies) {
    throw new Error(`No currencies found for chain ${chainId}`);
  }

  const feeCurrency = currencies.find(currency => currency.isFeeCurrency);
  const gasPriceStep = feeCurrency?.gasPriceStep ?? {
    low: 0.01,
    average: 0.025,
    high: 0.04,
  };

  return {
    amount: Decimal.fromUserInput(
      String(gasPriceStep.average),
      feeCurrency?.coinDecimals ?? 6,
    ),
    denom: feeCurrency?.coinMinimalDenom ?? 'uelys',
  };
}
